import moment from 'moment'
import React, { useRef, useCallback } from 'react'

import { ReactComponent as Clock } from '../../../../assets/svg/clock-counter.svg'
import { ReactComponent as Question } from '../../../../assets/svg/question.svg'
import { ReactComponent as Warning } from '../../../../assets/svg/warning-octagon.svg'
import { LoadingSkeleton } from '../../../../components/LoadingSkeleton'
import { useRequests } from '../../hooks'
import { Modal } from '../Modal'
import PixErrorSection from '../pix-components/pix-error'
import PixSuccessSection from '../pix-components/pix-success'
import { PixValueSubTitle } from '../pix-components/styles'

import CardNoRefund from './card-no-refund'
import CardPaymentOrder from './card-payment-order'
import CardPix from './card-pix'
import CardStarkbank from './card-starkbank'
import CardWirecard from './card-wirecard'
import { useCardRequestRefundFlow } from './hooks'
import * as S from './styles'

const CardRequest = ({ cridInfo, isMobile, onStatusChange, refetchParentData }) => {
  const modalRef = useRef()
  const modalGiveup = useRef()

  const openModal = useCallback(() => modalRef.current.openModalFn(), [])
  const closeModal = useCallback(() => modalRef.current.closeModalFn(), [])
  const openModalGiveup = useCallback(() => modalGiveup.current.openModalFn(), [])
  const closeModalGiveup = useCallback(() => modalGiveup.current.closeModalFn(), [])

  const { onSubmit, cancellationData, isLoading, isLoadingCancellationData, refundFlowStatus } = useRequests({
    cridInfo,
    closeModalGiveup,
    refetchParentData
  })

  const {
    setPixConfirmationData,
    markPersistHidden,
    persistPixKey,
    pixDisplay,
    showPixError,
    inPixUpdateFlow,
    canRevokeCancellation,
    refundInfo,
    refundAmount,
    refundType,
    productType,
    cancellationId,
    cancellationSubType
  } = useCardRequestRefundFlow({
    cancellationData,
    cridInfo,
    refundFlowStatus,
    onStatusChange
  })

  if (isLoadingCancellationData) {
    return <LoadingSkeleton isLoading hasHeading containerHeight="376px" containerWidth="704px" />
  }

  if (
    (!cancellationData && !isLoadingCancellationData) ||
    (!cancellationData?.cancellationRequest && !isLoadingCancellationData)
  ) {
    return <S.EmptyRequests>Você não tem nenhuma solicitação para acompanhar no momento.</S.EmptyRequests>
  }

  if (cancellationSubType && !cancellationSubType.includes('refund')) {
    return (
      <S.CardRequestContainer className="ph-no-capture">
        <S.CardRequestTitle>Acompanhamento da solicitação de cancelamento</S.CardRequestTitle>

        <S.CardRequestRow>
          <div>
            <strong>Curso:</strong> <span>{cridInfo?.course}</span>
          </div>
          <div>
            <strong>Instituição:</strong> <span>{cridInfo?.partner}</span>
          </div>
        </S.CardRequestRow>

        {cancellationData?.cancellationRequest?.status !== 'canceled' && (
          <div>
            <S.CardRequestSubTitle>
              {refundInfo.icon}
              {refundInfo.text}
            </S.CardRequestSubTitle>
          </div>
        )}
      </S.CardRequestContainer>
    )
  }

  return (
    <>
      <S.CardRequestContainer className="ph-no-capture">
        <S.CardRequestTitle>Acompanhamento da solicitação de cancelamento</S.CardRequestTitle>

        <S.CardRequestRow>
          <div>
            <strong>Curso:</strong> <span>{cridInfo?.course}</span>
          </div>
          <div>
            <strong>Instituição:</strong> <span>{cridInfo?.partner}</span>
          </div>
        </S.CardRequestRow>

        {!inPixUpdateFlow && (
          <S.CardRequestRow>
            <div>
              {isMobile ? <strong>Data do cancelamento:</strong> : <strong>Data do pedido de cancelamento: </strong>}
              <span>{moment(cancellationData?.cancellationRequest?.requestDate).format('DD/MM/YYYY')}</span>
            </div>
            <div>
              <strong>Motivo:</strong> <span>{cancellationData?.cancellationRequest?.comment}</span>
            </div>
          </S.CardRequestRow>
        )}

        {cancellationData?.cancellationRequest?.status !== 'canceled' && (
          <div>
            <S.CardRequestSubTitle>
              {refundInfo.icon}
              {refundInfo.text}
            </S.CardRequestSubTitle>
          </div>
        )}

        {!refundType && cancellationData?.cancellationRequest?.status !== 'canceled' && <CardNoRefund />}

        {refundType === 'starkbank' && cancellationData?.cancellationRequest?.status !== 'canceled' && (
          <CardStarkbank data={cancellationData} />
        )}

        {refundType === 'wirecard' && productType === 'Pix' && cancellationData?.cancellationRequest?.status !== 'canceled' && (
          <CardPix data={cancellationData} />
        )}

        {refundType === 'wirecard' &&
          productType === 'CreditCard' &&
          cancellationData?.cancellationRequest?.status !== 'canceled' && <CardWirecard data={cancellationData} />}

        {refundType === 'ordem de pagamento' && cancellationData?.cancellationRequest?.status !== 'canceled' && (
          <CardPaymentOrder data={cancellationData} />
        )}

        {showPixError ? (
          <PixErrorSection
            value={refundAmount}
            cancellationData={cancellationData}
            cancellationId={cancellationId}
            onPixUpdated={data => {
              const confirmed = { success: true, ...data }
              setPixConfirmationData(confirmed)
              try {
                if (persistPixKey) sessionStorage.setItem(persistPixKey, JSON.stringify(confirmed))
              } catch {}
              markPersistHidden()
            }}
          />
        ) : cancellationData?.cancellationRequest?.status === 'canceled' ? (
          <S.CardRequestInfo>
            <Warning />
            <S.CardRequestInfoText>
              <div>
                <strong>Você desistiu de cancelar a compra do curso:</strong> isso significa que nenhuma multa ou reembolso
                serão aplicados.
              </div>
            </S.CardRequestInfoText>
          </S.CardRequestInfo>
        ) : (
          <>
            {pixDisplay?.success && (
              <>
                <PixValueSubTitle>
                  <strong>Valor:</strong>
                  <span> R$ {refundAmount}</span>
                </PixValueSubTitle>

                {pixDisplay?.pixKey && (
                  <PixValueSubTitle>
                    <strong>Chave PIX:</strong>
                    <span> {pixDisplay?.pixKey}</span>
                  </PixValueSubTitle>
                )}

                {pixDisplay?.recipient && (
                  <PixValueSubTitle>
                    <strong>Beneficiário:</strong>
                    <span> {pixDisplay?.recipient}</span>
                  </PixValueSubTitle>
                )}

                <PixSuccessSection cancellationData={cancellationData} />
              </>
            )}

            {canRevokeCancellation && (
              <S.CardRequestInfo>
                <Clock />
                <S.CardRequestInfoText>
                  <div>
                    <strong>Seu cancelamento está em análise: </strong> O processo leva até 3 dias úteis. Durante esse período,
                    você pode desistir do cancelamento, se desejar. Após a confirmação dos dados, o valor do reembolso será
                    enviado imediatamente.{' '}
                  </div>
                  <a onClick={openModalGiveup} data-test-id="give-up-cancellation">
                    Desistir do cancelamento
                  </a>
                </S.CardRequestInfoText>
              </S.CardRequestInfo>
            )}
          </>
        )}

        {!inPixUpdateFlow && (
          <S.CardRequestCancellation onClick={openModal} data-test-id="modal-doubts">
            <Question />
            Dúvidas sobre o cancelamento
          </S.CardRequestCancellation>
        )}
      </S.CardRequestContainer>

      <Modal modalRef={modalRef} title="Dúvidas?" onClose={closeModal} showDoubtsFooter>
        <S.ModalBody>
          <p data-test-id="modal-doubts-body">
            Você pode consultar as principais dúvidas no nosso FAQ de cancelamento. Mas, se você ainda tiver alguma pergunta que
            não encontrou a resposta, você pode entrar em contato diretamente com o nosso atendimento.
          </p>
        </S.ModalBody>
      </Modal>

      <Modal
        modalRef={modalGiveup}
        title="Você vai continuar com a gente? 👀 "
        isLoading={isLoading}
        onClose={closeModalGiveup}
        onSubmit={onSubmit}
      >
        <S.ModalBody>
          <p data-test-id="give-up-cancellation-body">
            Você clicou para desistir do cancelamento. Isso significa que você irá continuar com o curso e nenhuma multa ou
            reembolso serão aplicados.
          </p>
        </S.ModalBody>
      </Modal>
    </>
  )
}

export default CardRequest
